import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

interface InfoTooltipProps {
  children: React.ReactNode;
  position?: 'top' | 'bottom';
  ariaLabel?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  children,
  position = 'bottom',
  ariaLabel = "Plus d'informations",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const gap = 8;

    // Position tooltip centered under button using transform
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;

    if (position === 'top') {
      const top = buttonRect.top - gap;
      setTooltipStyle({
        position: 'fixed',
        top: 'auto',
        bottom: window.innerHeight - top,
        left: buttonCenterX,
        transform: 'translateX(-50%)',
        zIndex: 9999,
      });
      setArrowStyle({
        position: 'absolute',
        bottom: -4,
        left: '50%',
        marginLeft: -4,
        width: 8,
        height: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderTop: 'none',
        borderLeft: 'none',
        transform: 'rotate(45deg)',
      });
    } else {
      const top = buttonRect.bottom + gap;
      setTooltipStyle({
        position: 'fixed',
        top,
        left: buttonCenterX,
        transform: 'translateX(-50%)',
        zIndex: 9999,
      });
      setArrowStyle({
        position: 'absolute',
        top: -4,
        left: '50%',
        marginLeft: -4,
        width: 8,
        height: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderBottom: 'none',
        borderRight: 'none',
        transform: 'rotate(45deg)',
      });
    }
  }, [position]);

  useEffect(() => {
    if (isOpen) {
      // Initial position with default width
      updatePosition();

      // Recompute after render to use actual tooltip dimensions
      const rafId = requestAnimationFrame(() => {
        updatePosition();
      });

      const handleClickOutside = (e: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(e.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
          buttonRef.current?.focus();
        }
      };

      const handleScroll = () => {
        updatePosition();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        cancelAnimationFrame(rafId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, updatePosition]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="info-tooltip-button"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
      >
        i
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            style={tooltipStyle}
          >
            <div className="info-tooltip-content">
              {children}
              <div style={arrowStyle} />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
